import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Grid,
} from '@mui/material';
import {
  Check as CheckIcon,
  Stars as StarsIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { SUBSCRIPTION_TIERS, getRemainingQuota } from '../config/subscriptionTiers';
import type { SubscriptionTier } from '../config/subscriptionTiers';
import { useCreateCheckout, useCustomerPortal, useSubscriptionStatus } from '../hooks/useSubscription';

export const Subscription = () => {
  const { t } = useTranslation('subscription');
  const [searchParams, setSearchParams] = useSearchParams();
  const createCheckout = useCreateCheckout();
  const customerPortal = useCustomerPortal();
  const { data: subscriptionData, isLoading, error, refetch } = useSubscriptionStatus();

  // Handle success callback from Dodo Payments
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      toast.success(t('subscriptionActivated'));
      // Refresh subscription status
      refetch();
      // Clean up URL
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch, t]);
  
  // Use real subscription data from API; normalize legacy 'basic'/'premium' to 'pro'
  const rawTier = subscriptionData?.tier || 'free';
  const currentTier: SubscriptionTier = (rawTier === 'basic' || rawTier === 'premium') ? 'pro' : rawTier as SubscriptionTier;
  const currentTierInfo = SUBSCRIPTION_TIERS[currentTier];
  const usageStats = subscriptionData?.usage;
  const limits = subscriptionData?.limits;

  // Show loading state
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>{t('loadingDetails')}</Typography>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {t('errorLoading')}
        </Alert>
      </Container>
    );
  }

  const handleSubscribe = async (tier: SubscriptionTier) => {
    if (tier === 'free') return;
    
    const tierInfo = SUBSCRIPTION_TIERS[tier];
    
    // Check if priceId is configured
    if (!tierInfo.priceId) {
      alert(t('priceIdNotConfigured', { tier }));
      return;
    }
    
    // Create checkout session with return URL
    const returnUrl = `${window.location.origin}/subscription?success=true`;
    
    createCheckout.mutate({
      priceId: tierInfo.priceId,
      returnUrl,
    });
  };

  const handleManageSubscription = () => {
    customerPortal.mutate();
  };

  const renderUsageBar = (label: string, used: number, limit: number | 'unlimited', monthly = true) => {
    if (limit === 'unlimited') {
      return (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {t('unlimited')}
            </Typography>
          </Box>
        </Box>
      );
    }

    const percentage = (used / limit) * 100;
    const remaining = getRemainingQuota(used, limit);
    const isNearLimit = percentage >= 80;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {used} / {limit}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={isNearLimit ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t(monthly ? 'remaining' : 'remainingTotal', { count: typeof remaining === 'number' ? remaining : 0 })}
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        {t('subscriptionPlans')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ mb: 4 }}>
        {t('chooseYourPlan')}
      </Typography>

      {/* Current Plan & Usage */}
      {currentTier && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6">{t('currentPlan', { plan: currentTierInfo.name })}</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentTierInfo.description}
              </Typography>
            </Box>
            {currentTier !== 'free' && (
              <Button 
                variant="outlined" 
                onClick={handleManageSubscription}
                disabled={customerPortal.isPending}
              >
                {customerPortal.isPending ? t('loading') : t('manageSubscription')}
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            {t('yourUsageThisMonth')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderUsageBar(
                t('projects'),
                usageStats?.projectsCount || 0,
                limits?.projects === -1 ? 'unlimited' : (limits?.projects || 0),
                false
              )}
              {renderUsageBar(
                t('podcastEpisodes'),
                usageStats?.episodesCount || 0,
                limits?.episodes === -1 ? 'unlimited' : (limits?.episodes || 0)
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderUsageBar(
                t('autoTranscriptions'),
                usageStats?.autoTranscriptionsCount || 0,
                limits?.autoTranscriptions === -1 ? 'unlimited' : (limits?.autoTranscriptions || 0)
              )}
              {renderUsageBar(
                t('autoTranslations'),
                usageStats?.autoTranslationsCount || 0,
                limits?.autoTranslations === -1 ? 'unlimited' : (limits?.autoTranslations || 0)
              )}
            </Grid>
          </Grid>

          {limits && !limits.isUnlimited && usageStats && (
            (usageStats.autoTranscriptionsCount >= (limits.autoTranscriptions * 0.8) ||
             usageStats.autoTranslationsCount >= (limits.autoTranslations * 0.8)) && 
            currentTier !== 'pro' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('nearLimitWarning')}
              </Alert>
            )
          )}
        </Paper>
      )}

      {/* Pricing Tiers - Only show for free tier users */}
      {currentTier === 'free' && (
        <>
          <Typography variant="h5" gutterBottom align="center" sx={{ mt: 2, mb: 3 }}>
            {t('chooseYourPlan')}
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Object.values(SUBSCRIPTION_TIERS).map((tier) => (
              <Grid size={{ xs: 12, md: 6 }} key={tier.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: tier.popular ? 2 : 1,
                    borderColor: tier.popular ? 'primary.main' : 'divider',
                    transform: tier.popular ? 'scale(1.05)' : 'none',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: tier.popular ? 'scale(1.05)' : 'scale(1.02)',
                    },
                  }}
                >
                  {tier.popular && (
                    <Chip
                      icon={<StarsIcon />}
                      label={t('mostPopular')}
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                      }}
                    />
                  )}
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      {tier.name}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h3" component="span" fontWeight="bold">
                        ${tier.price}
                      </Typography>
                      {tier.price > 0 && (
                        <Typography variant="body2" component="span" color="text.secondary">
                          /month
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {tier.description}
                    </Typography>

                    <List dense sx={{ mb: 2 }}>
                      {tier.features.map((feature, index) => (
                        <ListItem key={index} disableGutters>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        variant={tier.id === currentTier ? 'outlined' : tier.popular ? 'contained' : 'outlined'}
                        fullWidth
                        disabled={tier.id === currentTier || createCheckout.isPending}
                        onClick={() => handleSubscribe(tier.id)}
                      >
                        {tier.id === currentTier
                          ? t('currentPlanButton')
                          : tier.price === 0
                          ? t('getStarted')
                          : createCheckout.isPending
                          ? t('loading')
                          : t('upgrade')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* For Pro users, show success message */}
      {currentTier === 'pro' && (
        <Alert severity="success" sx={{ mb: 4 }}>
          {t('premiumSuccess')}
        </Alert>
      )}

      {/* FAQ or Additional Info */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('faq')}
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('faqChangePlansTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('faqChangePlansAnswer')}
          </Typography>

          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('faqLimitsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('faqLimitsAnswer')}
          </Typography>

          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('faqCalculationTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('faqCalculationAnswer')}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};
